/**
 * Category Section Component
 * Horizontal scrollable category navigation with product images
 * Displays collections from the collections-go API
 */

'use client';

import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { useTheme } from '@template-project/afrodita-components';
import { useHomeCollections, HomeCollectionWithImage } from '@/lib/hooks';
import { useTranslation } from '@/lib/i18n';
import { useIntl } from '@/lib/intl';

const Section = styled.section`
  padding: ${({ theme }) => theme.space['3xl']} 0;
  background-color: ${({ theme }) => theme.colors.background.primary};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.variants.heading2.fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.space.xl};
`;

const Container = styled.div`
  max-width: ${({ theme }) => theme.mediaQueries.desktop}px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.space.xl};
`;

const ScrollContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.md};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: ${({ theme }) => theme.mediaQueries.md}) {
    justify-content: center;
    overflow-x: visible;
    flex-wrap: wrap;
  }
`;

const CategoryCard = styled(Link)`
  flex: 0 0 auto;
  width: 120px;
  text-decoration: none;
  scroll-snap-align: start;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-4px);
  }
  
  &:hover .category-image {
    transform: scale(1.05);
  }
  
  @media (min-width: ${({ theme }) => theme.mediaQueries.md}) {
    width: 140px;
  }
`;

const CategoryImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: ${({ theme }) => theme.radius.regular};
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background.secondary};
`;

interface CategoryImageProps {
  $loaded: boolean;
}

const CategoryImage = styled.img<CategoryImageProps>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease, opacity 0.3s ease;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    opacity: 1;
  }
`;

const PlaceholderImage = styled.div<{ index: number }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  background: ${({ index }) => {
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
      'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
      'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
    ];
    return gradients[index % gradients.length];
  }};
`;

const CategoryLabel = styled.div`
  margin-top: ${({ theme }) => theme.space.sm};
  text-align: center;
`;

const CategoryName = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.4;
`;

const ProductCount = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const LoadingContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.md};
  overflow-x: auto;
  justify-content: center;
`;

const LoadingSkeleton = styled.div`
  flex: 0 0 auto;
  width: 120px;
  
  @media (min-width: ${({ theme }) => theme.mediaQueries.md}) {
    width: 140px;
  }
`;

const SkeletonImage = styled.div`
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: ${({ theme }) => theme.radius.regular};
  background: linear-gradient(90deg, 
    ${({ theme }) => theme.colors.background.secondary} 0%, 
    ${({ theme }) => theme.colors.background.primary} 50%, 
    ${({ theme }) => theme.colors.background.secondary} 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const SkeletonText = styled.div`
  width: 80%;
  height: 14px;
  margin: ${({ theme }) => theme.space.sm} auto 0;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.background.secondary};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

interface CollectionCardItemProps {
  collection: HomeCollectionWithImage;
  index: number;
}

function CollectionCardItem({ collection, index }: CollectionCardItemProps) {
  const theme = useTheme();
  const { localizedPath } = useIntl();
  const [imageLoaded, setImageLoaded] = useState(false);

  // Use displayImage (which includes product images) or fall back to imageUri
  const imageToDisplay = collection.displayImage || collection.imageUri;

  return (
    <CategoryCard to={localizedPath(`/collections/${collection.slug}`)} theme={theme}>
      <CategoryImageWrapper theme={theme}>
        {imageToDisplay ? (
          <CategoryImage
            className="category-image"
            src={imageToDisplay}
            alt={collection.title}
            loading="lazy"
            $loaded={imageLoaded}
            onLoad={() => setImageLoaded(true)}
          />
        ) : (
          <PlaceholderImage index={index}>
            {collection.title.charAt(0).toUpperCase()}
          </PlaceholderImage>
        )}
      </CategoryImageWrapper>
      <CategoryLabel theme={theme}>
        <CategoryName theme={theme}>
          {collection.title}
        </CategoryName>
        <ProductCount theme={theme}>
          {collection.productCount} {collection.productCount === 1 ? 'product' : 'products'}
        </ProductCount>
      </CategoryLabel>
    </CategoryCard>
  );
}

function LoadingState() {
  const theme = useTheme();
  
  return (
    <LoadingContainer theme={theme}>
      {Array.from({ length: 8 }).map((_, index) => (
        <LoadingSkeleton key={index} theme={theme}>
          <SkeletonImage theme={theme} />
          <SkeletonText theme={theme} />
        </LoadingSkeleton>
      ))}
    </LoadingContainer>
  );
}

export function CategorySection() {
  const theme = useTheme();
  const { collections, loading, error } = useHomeCollections();
  const { t } = useTranslation();
  
  return (
    <Section theme={theme}>
      <Container theme={theme}>
        <SectionTitle theme={theme}>{t('home.shopByCategory')}</SectionTitle>

        {loading && <LoadingState />}

        {error && (
          <ErrorMessage theme={theme}>
            Unable to load categories. Please try again later.
          </ErrorMessage>
        )}

        {!loading && !error && collections.length === 0 && (
          <ErrorMessage theme={theme}>
            No categories available at this time.
          </ErrorMessage>
        )}

        {!loading && !error && collections.length > 0 && (
          <ScrollContainer theme={theme}>
            {collections.map((collection, index) => (
              <CollectionCardItem
                key={collection.id}
                collection={collection}
                index={index}
              />
            ))}
          </ScrollContainer>
        )}
      </Container>
    </Section>
  );
}
